import { getAccessToken } from '@/lib/tokenStorage';

import { enhancedApi } from '../api/baseApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type TechRelationship = {
  relationshipId: number;
  siteId: string;
  siteName?: string | null;
  techId: string;
  techName?: string | null;
  herdTechId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

export type CreateTechRelationshipRequest = {
  siteId: string;
  techId: string;
  herdTechId: string;
  startDate?: string | null;
  endDate?: string | null;
};

export type UpdateTechRelationshipRequest = {
  herdTechId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

async function callTechRelationships<T>(
  path: string,
  method: 'GET' | 'PUT' | 'POST' | 'DELETE' = 'GET',
  body?: unknown,
): Promise<{ data?: T; error?: { status: number | string; data: string } }> {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.text();
      return { error: { status: response.status, data: error } };
    }

    if (response.status === 204) {
      return { data: undefined as T };
    }

    const data = (await response.json()) as T;
    return { data };
  } catch (error: unknown) {
    return {
      error: {
        status: 'FETCH_ERROR',
        data: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
}

export const techRelationshipsApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getTechRelationships: build.query<
      TechRelationship[],
      { siteId?: string; techId?: string }
    >({
      queryFn: async (args) => {
        const query = new URLSearchParams();
        if (args.siteId) query.set('siteId', args.siteId);
        if (args.techId) query.set('techId', args.techId);
        const qs = query.toString();
        return callTechRelationships<TechRelationship[]>(
          `/api/tech-relationships${qs ? `?${qs}` : ''}`,
        );
      },
      providesTags: ['TechRelationships'],
    }),
    createTechRelationship: build.mutation<
      { relationshipId: number },
      CreateTechRelationshipRequest
    >({
      queryFn: async (body) =>
        callTechRelationships<{ relationshipId: number }>(
          '/api/tech-relationships',
          'POST',
          body,
        ),
      invalidatesTags: ['TechRelationships'],
    }),
    updateTechRelationship: build.mutation<
      void,
      { relationshipId: number; body: UpdateTechRelationshipRequest }
    >({
      queryFn: async ({ relationshipId, body }) =>
        callTechRelationships<void>(
          `/api/tech-relationships/${relationshipId}`,
          'PUT',
          body,
        ),
      invalidatesTags: ['TechRelationships'],
    }),
    deleteTechRelationship: build.mutation<void, number>({
      queryFn: async (relationshipId) =>
        callTechRelationships<void>(
          `/api/tech-relationships/${relationshipId}`,
          'DELETE',
        ),
      invalidatesTags: ['TechRelationships'],
    }),
  }),
});

export const {
  useGetTechRelationshipsQuery,
  useCreateTechRelationshipMutation,
  useUpdateTechRelationshipMutation,
  useDeleteTechRelationshipMutation,
} = techRelationshipsApi;
